import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import PropertyTable from '../components/PropertyTable';
import { getAllPropertyUnits } from '../utils/api';
import './DatabasePage.css';

function DatabasePage() {
  const [allDatabaseProperties, setAllDatabaseProperties] = useState([]);
  const [databaseLoading, setDatabaseLoading] = useState(false);
  const [databaseError, setDatabaseError] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [searchTerm, setSearchTerm] = useState('');

  // Load all database properties on component mount
  useEffect(() => {
    loadAllDatabaseProperties();
  }, []);

  const loadAllDatabaseProperties = async () => {
    setDatabaseLoading(true);
    setDatabaseError(null);
    
    try {
      const data = await getAllPropertyUnits();
      setAllDatabaseProperties(data || []);
    } catch (err) {
      console.error('Error loading database properties:', err);
      setDatabaseError('Failed to load database properties');
    } finally {
      setDatabaseLoading(false);
    }
  };

  // Filter properties based on search term
  const filteredProperties = allDatabaseProperties.filter(property => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (property.project_name && property.project_name.toLowerCase().includes(term)) ||
      (property.unit_type && property.unit_type.toLowerCase().includes(term)) ||
      (property.finishing_type && property.finishing_type.toLowerCase().includes(term))
    );
  });

  return (
    <div className="database-page">
      <div className="page-header">
        <h1>🗄️ Property Database</h1>
        <p className="page-description">
          View all property units stored in the database. Use the search to filter properties.
        </p>
      </div>

      {/* Database State Section */}
      <div className="database-section">
        <div className="database-header">
          <h2>
            📊 All Properties 
            <span className="count-badge">
              {filteredProperties.length} {filteredProperties.length !== allDatabaseProperties.length && `of ${allDatabaseProperties.length}`} properties
            </span>
          </h2>
          <div className="header-controls">
            <div className="view-toggle">
              <button 
                onClick={() => setViewMode('table')}
                className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              >
                📊 Table
              </button>
              <button 
                onClick={() => setViewMode('cards')}
                className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
              >
                🎴 Cards
              </button>
            </div>
            <button 
              onClick={loadAllDatabaseProperties} 
              disabled={databaseLoading}
              className="refresh-button"
            >
              {databaseLoading ? '� Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {/* Search Box - Full Width Below Header */}
        <div className="search-row">
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Search by project, unit type, finishing..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {databaseLoading && (
          <LoadingSpinner message="Loading all database properties..." />
        )}

        {databaseError && (
          <div className="error-message">
            <strong>Database Error:</strong> {databaseError}
          </div>
        )}

        {filteredProperties.length > 0 && !databaseLoading && (
          <div className="database-properties">
            {viewMode === 'table' ? (
              <div className="table-container">
                <PropertyTable properties={filteredProperties} />
              </div>
            ) : (
              <div className="properties-grid">
                {filteredProperties.map((property, index) => (
                  <div key={index} className="property-card">
                    <div className="property-header">
                      <h3>{property.project_name || 'Unknown Project'}</h3>
                      <span className="unit-type">{property.unit_type || 'N/A'}</span>
                    </div>
                    <div className="property-details">
                      <p><strong>💰 Price:</strong> {property.total_price ? `${property.total_price.toLocaleString()} EGP` : 'N/A'}</p>
                      <p><strong>📐 Area:</strong> {property.area ? `${property.area} m²` : 'N/A'}</p>
                      <p><strong>🏠 Rooms:</strong> {property.no_of_rooms || 'N/A'}</p>
                      <p><strong>🎯 Finishing:</strong> {property.finishing_type || 'N/A'}</p>
                      <p><strong>📅 Delivery:</strong> {property.delivery_date || 'N/A'}</p>
                      <p><strong>💳 Down Payment:</strong> {property.down_payment ? `${property.down_payment.toLocaleString()} EGP` : 'N/A'}</p>
                      <p><strong>📆 Installment Years:</strong> {property.installment_no_of_years || 'N/A'}</p>
                    </div>
                    <div className="property-footer">
                      <span className="confidence">
                        Confidence: {Math.round((property.confidence_score || 0) * 100)}%
                      </span>
                      {property.last_updated && (
                        <span className="updated">
                          Updated: {new Date(property.last_updated).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {filteredProperties.length === 0 && !databaseLoading && !databaseError && (
          <div className="no-data">
            {searchTerm ? (
              <p>🔍 No properties found matching "{searchTerm}". Try a different search term.</p>
            ) : (
              <p>📭 No properties found in the database. Add projects and upload availability data to get started!</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DatabasePage;
